import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Flag, Award, Star, Users, CheckCircle 
} from "lucide-react";
import { LOGO_URL, BRAND } from "../common/constants";

const VeteransPage = () => {
  const [copied, setCopied] = useState(null);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      toast.success(`Code ${code} copied to clipboard!`);
    } catch (err) {
      setCopied(code);
      toast.info(`Your code is: ${code}`);
    }
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt={BRAND.parent} className="h-12 w-auto rounded-md" />
          </Link>
          <Link to="/resume">
            <Button className="bg-gradient-to-r from-red-600 to-blue-600 hover:from-red-700 hover:to-blue-700 text-white">
              Use My Discount
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero with American Flag Background */}
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/90 via-[#071734] to-white" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-red-600 via-white to-red-600" style={{ backgroundSize: '60px 100%', backgroundRepeat: 'repeat-x' }} />
        </div>
        
        <div className="absolute top-20 left-0 w-1/3 h-48 flex flex-wrap items-center justify-center gap-2 opacity-20">
          {[...Array(50)].map((_, i) => (
            <Star key={i} className="w-3 h-3 text-white fill-white" />
          ))}
        </div>

        <div className="relative container-main py-20 lg:py-28">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Flag className="w-10 h-10 text-red-500" />
              <Award className="w-14 h-14 text-white" />
              <Flag className="w-10 h-10 text-blue-400" />
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold tracking-tight">
              <span className="text-red-500">We Honor</span>{" "}
              <span className="text-white">Those Who</span>{" "}
              <span className="text-blue-400">Serve</span>
            </h1>
            
            <div className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto space-y-4">
              <p>
                The Founder of {BRAND.parent} not only served — <strong className="text-white">three of his four brothers served as well</strong>. 
                His Father, a now retired <strong className="text-white">Lieutenant Commander in the Navy</strong>, instilled in our family 
                a deep respect for the commitment and dedication it takes to serve our country.
              </p>
              <p className="text-blue-300 font-medium">
                Honoring veterans, active-duty service members, and the families who support them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Iwo Jima Tribute Section */}
      <section className="py-16 bg-gradient-to-b from-[#071734] via-slate-800 to-gray-100">
        <div className="container-main">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-red-600/20 via-white/10 to-blue-600/20 rounded-lg flex items-center justify-center border border-white/10">
                  <div className="text-center space-y-4">
                    <Users className="w-24 h-24 text-white/80 mx-auto" />
                    <div className="flex justify-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className={`w-1 rounded ${i % 3 === 0 ? 'bg-red-500/60' : i % 3 === 1 ? 'bg-white/60' : 'bg-blue-500/60'}`} style={{ height: `${60 + i * 10}px` }} />
                      ))}
                    </div>
                    <Flag className="w-12 h-12 text-red-500 mx-auto" />
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl" />
                <div className="absolute -top-4 -left-4 w-32 h-32 bg-red-500/10 rounded-full blur-2xl" />
              </div>

              <div className="space-y-6">
                <h2 className="text-3xl font-heading font-bold text-white">From Our Family to Yours</h2>
                <p className="text-gray-300 text-lg">
                  As a way of showing our gratitude, here is a small token from our family to yours 
                  to help you discover your strengths, explore your possibilities, and prepare for your next career chapter.
                </p>
                <div className="p-6 bg-gradient-to-r from-red-600/10 via-white/5 to-blue-600/10 border border-white/20 rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <Star className="w-8 h-8 text-white fill-white" />
                    <div>
                      <div className="text-3xl font-mono font-bold text-white">50% OFF</div>
                      <div className="text-sm text-gray-300">Complete or Career Profiler Package</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-300">
                    <strong className="text-white">Highly recommended for service members:</strong> The Career Profiler 
                    helps you see how your experience and transferable skills can fit civilian careers or a new industry.
                    Explore your strengths, career positioning, and job-search strategy — including skills you may not have considered.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Discount Codes Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-heading font-bold mb-4 text-[#071734]">Your Exclusive Discount Codes</h2>
              <p className="text-gray-600">Click any code to copy, then use it at checkout</p>
            </div>

            <div className="grid gap-4">
              <Card className="border-red-500/50 bg-gradient-to-r from-red-50 via-white to-blue-50">
                <CardContent className="py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <Badge className="bg-red-600 text-white mb-2">50% OFF - Complete & Career Profiler</Badge>
                      <p className="text-sm text-gray-600">For all who have served or are currently serving</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {["VETERAN50", "HOOAH", "OORAH", "HOOYAH"].map((code) => (
                        <Button
                          key={code}
                          variant="outline"
                          className={`font-mono ${copied === code ? 'bg-green-100 border-green-500 text-green-700' : 'border-red-500/50 hover:bg-red-50 text-[#071734]'}`}
                          onClick={() => copyCode(code)}
                          data-testid={`copy-${code}`}
                        >
                          {copied === code ? <CheckCircle className="w-4 h-4 mr-1" /> : null}
                          {code}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-[#00a3e0]/40 bg-white" data-testid="family-discount-offer">
                <CardContent className="py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <Badge className="bg-[#071734] text-white mb-2">50% OFF — Military &amp; Veteran Families</Badge>
                      <p className="text-sm text-gray-700">For family members of U.S. veterans and active-duty service members.</p>
                      <p className="text-sm text-gray-600 mt-1">Applies to the Complete and Career Profiler packages. Use at checkout; discounts do not stack.</p>
                    </div>
                    <Button
                      variant="outline"
                      className={`font-mono ${copied === 'FAMILY50' ? 'bg-green-100 border-green-500 text-green-700' : 'border-[#00a3e0]/50 hover:bg-blue-50 text-[#071734]'}`}
                      onClick={() => copyCode('FAMILY50')}
                      data-testid="copy-FAMILY50"
                    >
                      {copied === 'FAMILY50' ? <CheckCircle className="w-4 h-4 mr-2" /> : null}
                      FAMILY50
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-blue-500/30 bg-white">
                <CardContent className="py-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                      <Badge variant="outline" className="border-blue-500/50 text-blue-600 mb-2">20% OFF - All Packages</Badge>
                      <p className="text-sm text-gray-600">Alternative codes that work on any package</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {["VETERAN20", "THANKYOU", "SERVED"].map((code) => (
                        <Button
                          key={code}
                          variant="outline"
                          size="sm"
                          className={`font-mono ${copied === code ? 'bg-green-100 border-green-500 text-green-700' : 'border-blue-500/30 hover:bg-blue-50 text-[#071734]'}`}
                          onClick={() => copyCode(code)}
                          data-testid={`copy-${code}`}
                        >
                          {copied === code ? <CheckCircle className="w-3 h-3 mr-1" /> : null}
                          {code}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 text-center">
              <Link to="/resume">
                <Button 
                  size="lg"
                  className="text-lg px-8 bg-gradient-to-r from-red-600 via-blue-800 to-blue-600 hover:from-red-700 hover:to-blue-700 text-white"
                  data-testid="veterans-cta-btn"
                >
                  <Flag className="w-5 h-5 mr-2" />
                  Start My Resume — Use My Discount
                </Button>
              </Link>
              <p className="text-sm text-gray-600 mt-4">
                Thank you for your service. We're honored to help you on your next mission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-200 bg-white">
        <div className="container-main text-center">
          <p className="text-sm text-gray-600">
            © 2026 {BRAND.parent}. {BRAND.tagline} | <Link to="/" className="text-[#00a3e0] hover:underline">Back to Home</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VeteransPage;
